/* eslint-disable max-len */
import axios from 'axios';

import {API_BASE_URL, API_ENDPOINT_JSON} from '../../../config';
import {OPKey} from '../../../pages/configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import {subCategoryPathSegment} from '../../../utils/companyCategorySubCategory';
import {APIType, APITypeEnum} from '../../interface/tableAPIType';
import {tableAPIMethod} from '../../utils/tableAPIMethod';

/** Safe fallback when the pillars API fails or returns nothing. */
export const EMPTY_FRAMEWORK_PAYLOAD: Record<string, never> = {};

export const saveReportData = async (
  id: string,
  data: any = undefined
): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const userId = useUserStore.getState().userDetails?._id;

  const inputData = {
    id,
    userId,
    document: data
  };

  try {
    const res = await axios.request({
      method: 'post',
      url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.saveReportDetails}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: inputData ?? {}
    });

    return res.data;
  } catch (err) {}
};

export const gerReportDetails = async (
  type: APIType,
  id?: string | null
): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const companyID = useCompanyStore.getState().companyId;
  const userId = useUserStore.getState().userDetails?._id;

  try {
    const res = await axios.request({
      method: tableAPIMethod(APITypeEnum.GET_ONE),
      url: `${API_BASE_URL}${
        API_ENDPOINT_JSON.REPORT_BUILDER.getReportDetails
      }/${!id ? 'new' : id}/${companyID}/${userId}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: {}
    });

    return res.data.data;
  } catch (err) {}
};

export const gerReports = async (): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const companyID = useCompanyStore.getState().companyId;

  try {
    const res = await axios.request({
      method: tableAPIMethod(APITypeEnum.GET_ONE),
      url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.getReports}/${companyID}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: {}
    });

    return res.data.data;
  } catch (err) {}
};

export const deleteReport = async (id: string): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const userId = useUserStore.getState().userDetails?._id;

  try {
    const res = await axios.request({
      method: tableAPIMethod(APITypeEnum.METHOD_DELETE),
      url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.deleteReport}/${id}/${userId}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: {}
    });

    return res.data.data;
  } catch (err) {}
};

export const updateReportName = async (
  id: string,
  name: string
): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const userId = useUserStore.getState().userDetails?._id;

  try {
    const res = await axios.request({
      method: tableAPIMethod(APITypeEnum.PUT),
      url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.updateReportName}/${id}/${name}/${userId}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: {}
    });

    return res.data.data;
  } catch (err) {}
};

export const updateFavorite = async (
  id: string,
  favorite: boolean
): Promise<any> => {
  const token = useUserStore.getState().userToken;
  const userId = useUserStore.getState().userDetails?._id;

  const res = await axios.request({
    method: tableAPIMethod(APITypeEnum.PUT),
    url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.updateFavorite}/${id}/${favorite}/${userId}`,
    headers: {
      Authorization: `Bearer ${token}`
    },
    params: {},
    data: {}
  });

  return res.data?.data ?? res.data;
};

export const getFrameworkPillars = async (
  year: string,
  framework: string
): Promise<Record<string, unknown>> => {
  const token = useUserStore.getState().userToken;
  const companyID = useCompanyStore.getState().companyId;
  const subcategoryId = subCategoryPathSegment(
    useCompanyStore.getState().companyDetails?.[OPKey.SUB_CATEGORY]
  );

  try {
    const res = await axios.request({
      method: tableAPIMethod(APITypeEnum.GET_ONE),
      url: `${API_BASE_URL}${API_ENDPOINT_JSON.REPORT_BUILDER.getFrameworkPillars}/${companyID}/${year}/${framework}/${subcategoryId}`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {},
      data: {}
    });

    return res.data?.data ?? EMPTY_FRAMEWORK_PAYLOAD;
  } catch (err) {
    console.error(
      '[getFrameworkPillars] Failed to load framework pillars:',
      err
    );

    return EMPTY_FRAMEWORK_PAYLOAD;
  }
};
